import type React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  Layout,
  Palette,
  Type,
  Image as ImageIcon,
  BarChart4,
  ArrowRight,
  ArrowDown,
  Upload,
  Film,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Slide,
  ChartData,
  Theme,
  LayoutType,
} from "@/routes/~dashboard/~presentation/presentation-data";
import { useRef } from "react";

// Import chart components
import AreaChart from "@/components/charts/area-chart";
import BarChartComponent from "@/components/charts/bar-chart";
import LineChart from "@/components/charts/line-chart";
import PieChartComponent from "@/components/charts/pie-chart";
import DoughnutChart from "@/components/charts/doughnut-chart";

interface SlideEditorProps {
  slide: Slide;
  updateSlide: (slide: Slide) => void;
  theme: Theme;
}

// Define MediaFile type
interface MediaFile {
  file: File;
  previewUrl: string;
  type: "image" | "video";
}

// Component to handle file uploads for images and videos
const FileUpload = ({
  onFileSelect,
  mediaType = "image",
  currentMedia = null,
  onRemoveMedia,
}: {
  onFileSelect: (mediaFile: MediaFile) => void;
  mediaType?: "image" | "video";
  currentMedia?: MediaFile | null;
  onRemoveMedia?: () => void;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      const type = file.type.startsWith("image/") ? "image" : "video";
      onFileSelect({ file, previewUrl, type });
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="flex w-full flex-col items-center justify-center gap-2">
      {currentMedia ? (
        <div className="relative w-full">
          {currentMedia.type === "image" ? (
            <img
              src={currentMedia.previewUrl}
              alt="Preview"
              className="mx-auto max-h-[200px] rounded-md object-contain"
            />
          ) : (
            <video
              src={currentMedia.previewUrl}
              controls
              className="max-h-[200px] w-full rounded-md"
            />
          )}
          <Button
            size="sm"
            variant="ghost"
            className="absolute right-2 top-2 h-8 w-8 rounded-full bg-white/50 p-0 hover:bg-white/80"
            onClick={onRemoveMedia}
          >
            <X size={16} />
          </Button>
        </div>
      ) : (
        <div
          onClick={handleButtonClick}
          className="flex min-h-[120px] w-full cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-gray-300 bg-gray-50 p-6 transition-colors hover:bg-gray-100"
        >
          {mediaType === "image" ? (
            <>
              <ImageIcon className="h-10 w-10 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">
                Click to upload an image
              </p>
              <p className="text-xs text-gray-400">PNG, JPG, GIF up to 5MB</p>
            </>
          ) : (
            <>
              <Film className="h-10 w-10 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">
                Click to upload a video
              </p>
              <p className="text-xs text-gray-400">MP4, WebM up to 50MB</p>
            </>
          )}
        </div>
      )}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept={mediaType === "image" ? "image/*" : "video/*"}
      />
      {!currentMedia && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleButtonClick}
          className="mt-2"
        >
          <Upload className="mr-2 h-4 w-4" />
          {mediaType === "image" ? "Upload Image" : "Upload Video"}
        </Button>
      )}
    </div>
  );
};

const renderChartPreview = (chartData: ChartData | undefined) => {
  if (!chartData || !chartData.data) return null;

  const chartSeries = chartData.data.map((series) => ({
    name: series.name,
    data: series.labels.map((label, idx) => ({
      name: label,
      value: series.values[idx] || 0,
    })),
  }));

  const chartHeight = 120;
  const chartProps = {
    series: chartSeries,
    height: chartHeight,
    showGrid: false,
    animate: true,
    showLegend: false,
  };

  switch (chartData.type) {
    case "bar":
      return <BarChartComponent {...chartProps} />;
    case "line":
      return <LineChart {...chartProps} />;
    case "pie":
      return <PieChartComponent {...chartProps} />;
    case "doughnut":
      return <DoughnutChart {...chartProps} />;
    case "area":
      return <AreaChart {...chartProps} />;
    default:
      return (
        <div className="flex h-full items-center justify-center">
          Unsupported chart type
        </div>
      );
  }
};

export default function SlideEditor({
  slide,
  updateSlide,
  theme,
}: SlideEditorProps) {
  const handleLayoutChange = (value: string) => {
    const baseSlide = {
      id: slide.id,
      layout: value as LayoutType,
      title: slide.title,
      background: slide.background,
      titleColor: slide.titleColor,
      textColor: slide.textColor,
    };

    let newSlide: Slide;
    switch (value) {
      case "title-only":
      case "title-content":
      case "standard":
      case "split":
      case "image-text":
        newSlide = {
          ...baseSlide,
          content:
            "slide" in slide && "content" in slide
              ? slide.content
              : "Enter content here",
        };
        break;
      case "titleSlide":
        newSlide = {
          ...baseSlide,
          subtitle:
            "slide" in slide && "subtitle" in slide
              ? slide.subtitle
              : "Enter your subtitle here",
        };
        break;
      case "contentFull":
        newSlide = {
          ...baseSlide,
          subtitle:
            "slide" in slide && "subtitle" in slide
              ? slide.subtitle
              : "Enter your subtitle here",
          content:
            "slide" in slide && "content" in slide
              ? slide.content
              : "Enter content here",
        };
        break;
      case "contentTwoCol":
        newSlide = {
          ...baseSlide,
          subtitle:
            "slide" in slide && "subtitle" in slide
              ? slide.subtitle
              : "Enter your subtitle here",
          colLeft:
            "slide" in slide && "colLeft" in slide
              ? slide.colLeft
              : "Left column content",
          colRight:
            "slide" in slide && "colRight" in slide
              ? slide.colRight
              : "Right column content",
        };
        break;
      case "contentThreeCol":
        newSlide = {
          ...baseSlide,
          subtitle:
            "slide" in slide && "subtitle" in slide
              ? slide.subtitle
              : "Enter your subtitle here",
          colLeft:
            "slide" in slide && "colLeft" in slide
              ? slide.colLeft
              : "Left column content",
          colMiddle:
            "slide" in slide && "colMiddle" in slide
              ? slide.colMiddle
              : "Middle column content",
          colRight:
            "slide" in slide && "colRight" in slide
              ? slide.colRight
              : "Right column content",
        };
        break;
      case "contentFourCol":
        newSlide = {
          ...baseSlide,
          subtitle:
            "slide" in slide && "subtitle" in slide
              ? slide.subtitle
              : "Enter your subtitle here",
          col1:
            "slide" in slide && "col1" in slide
              ? slide.col1
              : "Column 1 content",
          col2:
            "slide" in slide && "col2" in slide
              ? slide.col2
              : "Column 2 content",
          col3:
            "slide" in slide && "col3" in slide
              ? slide.col3
              : "Column 3 content",
          col4:
            "slide" in slide && "col4" in slide
              ? slide.col4
              : "Column 4 content",
        };
        break;
      case "contentWithMediaRight":
      case "contentWithMediaLeft":
        newSlide = {
          ...baseSlide,
          subtitle:
            "slide" in slide && "subtitle" in slide
              ? slide.subtitle
              : "Enter your subtitle here",
          content:
            "slide" in slide && "content" in slide
              ? slide.content
              : "Enter content here",
          media:
            "slide" in slide && "media" in slide
              ? slide.media
              : { path: "./src/assets/image.jpg", type: "image" },
        };
        break;
      case "imageWithCaption":
        newSlide = {
          ...baseSlide,
          image:
            "slide" in slide && "image" in slide
              ? slide.image
              : { path: "./src/assets/image.jpg", type: "image" },
          caption:
            "slide" in slide && "caption" in slide
              ? slide.caption
              : "Enter image caption here",
        };
        break;
      case "videoWithCaption":
        newSlide = {
          ...baseSlide,
          media:
            "slide" in slide && "media" in slide
              ? slide.media
              : { path: "./src/assets/video.mp4", type: "video" },
          caption:
            "slide" in slide && "caption" in slide
              ? slide.caption
              : "Enter video caption here",
        };
        break;
      case "imageGrid":
        newSlide = {
          ...baseSlide,
          cell_1_1: { path: "./src/assets/image.jpg", type: "image" },
          cell_1_2: { path: "./src/assets/image.jpg", type: "image" },
          cell_1_3: { path: "./src/assets/image.jpg", type: "image" },
          cell_2_1: { path: "./src/assets/image.jpg", type: "image" },
          cell_2_2: { path: "./src/assets/image.jpg", type: "image" },
          cell_2_3: { path: "./src/assets/image.jpg", type: "image" },
        };
        break;
      case "table":
        newSlide = {
          ...baseSlide,
          subtitle:
            "slide" in slide && "subtitle" in slide
              ? slide.subtitle
              : "Table subtitle",
          table: {
            rows: [
              ["Header 1", "Header 2", "Header 3"],
              ["Row 1, Col 1", "Row 1, Col 2", "Row 1, Col 3"],
              ["Row 2, Col 1", "Row 2, Col 2", "Row 2, Col 3"],
            ],
            options: {
              columnWidths: [2, 1.5, 1.5],
              autoPageRepeat: false,
              autoPageSlides: false,
              fontFace: "Arial",
              fontSize: 12,
              color: "363636",
              border: { type: "solid", pt: 1, color: "808080" },
              headerRow: true,
              headerRowOptions: {
                fontFace: "Arial",
                fontSize: 14,
                bold: true,
                color: "FFFFFF",
                fill: { color: "404040" },
              },
              zebra: true,
              zebraColors: ["F5F5F5", "FFFFFF"],
            },
          },
        };
        break;
      case "chartSingle":
        newSlide = {
          ...baseSlide,
          subtitle:
            "slide" in slide && "subtitle" in slide
              ? slide.subtitle
              : "Chart subtitle",
          chart: {
            type: "line",
            data: [
              {
                name: "Sample Data Series",
                labels: ["Label 1", "Label 2", "Label 3", "Label 4", "Label 5"],
                values: [10, 20, 30, 40, 50],
              },
            ],
          },
          explanationTitle: "Key Insights",
          explanationContent: "• Insight 1\n• Insight 2\n• Insight 3",
        };
        break;
      case "chartDual":
        newSlide = {
          ...baseSlide,
          titleLeft: "Left Chart Title",
          titleRight: "Right Chart Title",
          chartLeft: {
            type: "bar",
            data: [
              {
                name: "Left Chart Data",
                labels: ["A", "B", "C", "D", "E"],
                values: [10, 20, 30, 40, 50],
              },
            ],
          },
          chartRight: {
            type: "pie",
            data: [
              {
                name: "Right Chart Data",
                labels: ["A", "B", "C", "D"],
                values: [25, 25, 25, 25],
              },
            ],
          },
          explanationLeft: "Left chart explanation",
          explanationRight: "Right chart explanation",
        };
        break;
      case "dashboard":
        newSlide = {
          ...baseSlide,
          chart1: {
            type: "line",
            data: [
              {
                name: "Chart 1 Data",
                labels: ["A", "B", "C", "D"],
                values: [10, 20, 30, 40],
              },
            ],
          },
          chart2: {
            type: "bar",
            data: [
              {
                name: "Chart 2 Data",
                labels: ["A", "B", "C", "D"],
                values: [40, 30, 20, 10],
              },
            ],
          },
          chart3: {
            type: "area",
            data: [
              {
                name: "Chart 3 Data",
                labels: ["A", "B", "C", "D"],
                values: [15, 25, 35, 45],
              },
            ],
          },
          chart4: {
            type: "pie",
            data: [
              {
                name: "Chart 4 Data",
                labels: ["A", "B", "C", "D"],
                values: [25, 25, 25, 25],
              },
            ],
          },
        };
        break;
      case "processHorizontal":
        newSlide = {
          ...baseSlide,
          step1Box: "Step 1",
          step2Box: "Step 2",
          step3Box: "Step 3",
          step4Box: "Step 4",
          step1Text: "Step 1 description",
          step2Text: "Step 2 description",
          step3Text: "Step 3 description",
          step4Text: "Step 4 description",
          arrow1: { type: "right-arrow", color: "#404040" },
          arrow2: { type: "right-arrow", color: "#404040" },
          arrow3: { type: "right-arrow", color: "#404040" },
        };
        break;
      case "processVertical":
        newSlide = {
          ...baseSlide,
          step1Box: "Step 1",
          step2Box: "Step 2",
          step3Box: "Step 3",
          step1Text: "Step 1 description",
          step2Text: "Step 2 description",
          step3Text: "Step 3 description",
          arrow1: { type: "down-arrow", color: "#404040" },
          arrow2: { type: "down-arrow", color: "#404040" },
        };
        break;
      case "timelineHorizontal":
        newSlide = {
          ...baseSlide,
          timelineBar: { color: "#404040" },
          milestone1: { color: "#5DA5DA" },
          milestone2: { color: "#FAA43A" },
          milestone3: { color: "#60BD68" },
          milestone4: { color: "#F17CB0" },
          milestone1Upper: "Milestone 1\nTitle",
          milestone2Upper: "Milestone 2\nTitle",
          milestone3Upper: "Milestone 3\nTitle",
          milestone4Upper: "Milestone 4\nTitle",
          milestone1Lower: "Milestone 1 description",
          milestone2Lower: "Milestone 2 description",
          milestone3Lower: "Milestone 3 description",
          milestone4Lower: "Milestone 4 description",
        };
        break;
      case "timelineVertical":
        newSlide = {
          ...baseSlide,
          timelineBar: { color: "#404040" },
          milestone1: { color: "#5DA5DA" },
          milestone2: { color: "#FAA43A" },
          milestone3: { color: "#60BD68" },
          milestone4: { color: "#F17CB0" },
          milestone1Date: "Date 1",
          milestone2Date: "Date 2",
          milestone3Date: "Date 3",
          milestone4Date: "Date 4",
          milestone1Text: "Milestone 1 description",
          milestone2Text: "Milestone 2 description",
          milestone3Text: "Milestone 3 description",
          milestone4Text: "Milestone 4 description",
        };
        break;
      case "contactInfo":
        newSlide = {
          ...baseSlide,
          thankYouText: "Thank You",
          contactInfo: "Contact Information",
        };
        break;
      default:
        newSlide = {
          ...baseSlide,
          content: "Enter content here",
        };
    }

    updateSlide(newSlide);
  };

  const getSlideStyle = () => {
    return {
      backgroundColor: slide.background || theme.slideBackground,
      color: slide.textColor || theme.textColor,
      fontFamily: theme.fontFamily,
      boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
      borderRadius: "8px",
      transition: "all 0.3s ease",
    };
  };

  const getLayoutClass = () => {
    switch (slide.layout) {
      case "title-only":
        return "flex flex-col justify-center items-center text-center";
      case "title-content":
        return "flex flex-col justify-start items-center";
      case "split":
        return "grid grid-cols-2 gap-8";
      case "image-text":
        return "grid grid-cols-2 gap-8";
      case "titleSlide":
        return "flex flex-col justify-center items-center text-center";
      case "contentFull":
        return "flex flex-col justify-start items-center";
      case "contentTwoCol":
        return "grid grid-cols-2 gap-8";
      case "contentThreeCol":
        return "grid grid-cols-3 gap-4";
      case "contentFourCol":
        return "grid grid-cols-4 gap-3";
      case "contentWithMediaRight":
        return "grid grid-cols-2 gap-8";
      case "contentWithMediaLeft":
        return "grid grid-cols-2 gap-8";
      case "imageWithCaption":
      case "videoWithCaption":
        return "flex flex-col justify-start items-center";
      case "imageGrid":
        return "grid grid-rows-2 grid-cols-3 gap-4";
      case "table":
        return "flex flex-col justify-start items-center";
      case "chartSingle":
        return "flex flex-col justify-start items-center";
      case "chartDual":
        return "grid grid-cols-2 gap-8";
      case "dashboard":
        return "gap-4 h-128 overflow-y-auto";
      case "processHorizontal":
        return "flex flex-col justify-start items-center";
      case "processVertical":
        return "flex flex-col justify-start items-center";
      case "timelineHorizontal":
        return "flex flex-col justify-start items-center";
      case "timelineVertical":
        return "flex flex-col justify-start items-center";
      case "contactInfo":
        return "flex flex-col justify-center items-center text-center";
      default:
        return "flex flex-col justify-start";
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSlide({ ...slide, title: e.target.value });
  };

  const handleSubtitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if ("subtitle" in slide) {
      updateSlide({ ...slide, subtitle: e.target.value });
    }
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if ("content" in slide) {
      updateSlide({ ...slide, content: e.target.value });
    }
  };

  const handleTextFieldChange = (field: string, value: string) => {
    updateSlide({ ...slide, [field]: value });
  };

  const renderSlideEditor = () => {
    switch (slide.layout) {
      case "standard":
      case "title-only":
      case "title-content":
      case "split":
      case "image-text":
        return (
          <>
            <Input
              value={slide.title}
              onChange={handleTitleChange}
              className="mb-6 w-full border-0 bg-transparent p-0 text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
              style={{ color: slide.titleColor || theme.titleColor }}
              placeholder="Enter slide title..."
            />
            {"content" in slide && (
              <Textarea
                value={slide.content}
                onChange={handleContentChange}
                className="w-full flex-1 resize-none border-0 bg-transparent p-0 text-xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                placeholder="Enter content here..."
                style={{ color: slide.textColor || theme.textColor }}
              />
            )}
          </>
        );

      case "titleSlide":
        return (
          <>
            <Input
              value={slide.title}
              onChange={handleTitleChange}
              className="mb-6 w-full border-0 bg-transparent p-0 text-5xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
              style={{ color: slide.titleColor || theme.titleColor }}
              placeholder="Enter presentation title..."
            />
            {"subtitle" in slide && (
              <Input
                value={slide.subtitle}
                onChange={handleSubtitleChange}
                className="w-full border-0 bg-transparent p-0 text-3xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                placeholder="Enter subtitle here..."
                style={{ color: slide.textColor || theme.textColor }}
              />
            )}
          </>
        );

      case "contentFull":
        return (
          <>
            <Input
              value={slide.title}
              onChange={handleTitleChange}
              className="mb-2 w-full border-0 bg-transparent p-0 text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
              style={{ color: slide.titleColor || theme.titleColor }}
              placeholder="Enter slide title..."
            />
            {"subtitle" in slide && (
              <Input
                value={slide.subtitle}
                onChange={handleSubtitleChange}
                className="mb-4 w-full border-0 bg-transparent p-0 text-2xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                placeholder="Enter subtitle here..."
                style={{ color: slide.textColor || theme.textColor }}
              />
            )}
            {"content" in slide && (
              <Textarea
                value={slide.content}
                onChange={handleContentChange}
                className="w-full flex-1 resize-none border-0 bg-transparent p-0 text-xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                placeholder="Enter content here..."
                style={{ color: slide.textColor || theme.textColor }}
              />
            )}
          </>
        );

      case "contentTwoCol":
        return (
          <>
            <div className="col-span-2 mb-4">
              <Input
                value={slide.title}
                onChange={handleTitleChange}
                className="mb-2 w-full border-0 bg-transparent p-0 text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                style={{ color: slide.titleColor || theme.titleColor }}
                placeholder="Enter slide title..."
              />
              {"subtitle" in slide && (
                <Input
                  value={slide.subtitle}
                  onChange={handleSubtitleChange}
                  className="w-full border-0 bg-transparent p-0 text-2xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Enter subtitle here..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
            <div>
              {"colLeft" in slide && (
                <Textarea
                  value={slide.colLeft}
                  onChange={(e) =>
                    handleTextFieldChange("colLeft", e.target.value)
                  }
                  className="h-full w-full resize-none border-0 bg-transparent p-0 text-xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Enter left column content..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
            <div>
              {"colRight" in slide && (
                <Textarea
                  value={slide.colRight}
                  onChange={(e) =>
                    handleTextFieldChange("colRight", e.target.value)
                  }
                  className="h-full w-full resize-none border-0 bg-transparent p-0 text-xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Enter right column content..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
          </>
        );

      case "contentThreeCol":
        return (
          <>
            <div className="col-span-3 mb-4">
              <Input
                value={slide.title}
                onChange={handleTitleChange}
                className="mb-2 w-full border-0 bg-transparent p-0 text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                style={{ color: slide.titleColor || theme.titleColor }}
                placeholder="Enter slide title..."
              />
              {"subtitle" in slide && (
                <Input
                  value={slide.subtitle}
                  onChange={handleSubtitleChange}
                  className="w-full border-0 bg-transparent p-0 text-2xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Enter subtitle here..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
            <div>
              {"colLeft" in slide && (
                <Textarea
                  value={slide.colLeft}
                  onChange={(e) =>
                    handleTextFieldChange("colLeft", e.target.value)
                  }
                  className="h-full w-full resize-none border-0 bg-transparent p-0 text-base outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Enter left column content..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
            <div>
              {"colMiddle" in slide && (
                <Textarea
                  value={slide.colMiddle}
                  onChange={(e) =>
                    handleTextFieldChange("colMiddle", e.target.value)
                  }
                  className="h-full w-full resize-none border-0 bg-transparent p-0 text-base outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Enter middle column content..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
            <div>
              {"colRight" in slide && (
                <Textarea
                  value={slide.colRight}
                  onChange={(e) =>
                    handleTextFieldChange("colRight", e.target.value)
                  }
                  className="h-full w-full resize-none border-0 bg-transparent p-0 text-base outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Enter right column content..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
          </>
        );

      case "contentFourCol":
        return (
          <>
            <div className="col-span-4 mb-4">
              <Input
                value={slide.title}
                onChange={handleTitleChange}
                className="mb-2 w-full border-0 bg-transparent p-0 text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                style={{ color: slide.titleColor || theme.titleColor }}
                placeholder="Enter slide title..."
              />
              {"subtitle" in slide && (
                <Input
                  value={slide.subtitle}
                  onChange={handleSubtitleChange}
                  className="w-full border-0 bg-transparent p-0 text-2xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Enter subtitle here..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
            <div>
              {"col1" in slide && (
                <Textarea
                  value={slide.col1}
                  onChange={(e) =>
                    handleTextFieldChange("col1", e.target.value)
                  }
                  className="h-full w-full resize-none border-0 bg-transparent p-0 text-sm outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Column 1 content..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
            <div>
              {"col2" in slide && (
                <Textarea
                  value={slide.col2}
                  onChange={(e) =>
                    handleTextFieldChange("col2", e.target.value)
                  }
                  className="h-full w-full resize-none border-0 bg-transparent p-0 text-sm outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Column 2 content..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
            <div>
              {"col3" in slide && (
                <Textarea
                  value={slide.col3}
                  onChange={(e) =>
                    handleTextFieldChange("col3", e.target.value)
                  }
                  className="h-full w-full resize-none border-0 bg-transparent p-0 text-sm outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Column 3 content..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
            <div>
              {"col4" in slide && (
                <Textarea
                  value={slide.col4}
                  onChange={(e) =>
                    handleTextFieldChange("col4", e.target.value)
                  }
                  className="h-full w-full resize-none border-0 bg-transparent p-0 text-sm outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Column 4 content..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
          </>
        );

      case "contentWithMediaRight":
        return (
          <>
            <div className="col-span-2 mb-4">
              <Input
                value={slide.title}
                onChange={handleTitleChange}
                className="mb-2 w-full border-0 bg-transparent p-0 text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                style={{ color: slide.titleColor || theme.titleColor }}
                placeholder="Enter slide title..."
              />
              {"subtitle" in slide && (
                <Input
                  value={slide.subtitle}
                  onChange={handleSubtitleChange}
                  className="mb-4 w-full border-0 bg-transparent p-0 text-2xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Enter subtitle here..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
            <div>
              {"content" in slide && (
                <Textarea
                  value={slide.content}
                  onChange={handleContentChange}
                  className="h-full w-full resize-none border-0 bg-transparent p-0 text-xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Enter content here..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
            <div className="flex flex-col items-center justify-center rounded-lg bg-gray-200 p-4">
              <FileUpload
                mediaType="image"
                onFileSelect={(mediaFile) => {
                  updateSlide({
                    ...slide,
                    media: {
                      type: mediaFile.type === "video" ? "video" : "image",
                      path: mediaFile.previewUrl,
                    },
                  });
                }}
                currentMedia={
                  slide.media &&
                  slide.media.path &&
                  slide.media.path.startsWith("blob:")
                    ? {
                        file: new File([], slide.media.type || "image"),
                        previewUrl: slide.media.path,
                        type: slide.media.type === "video" ? "video" : "image",
                      }
                    : undefined
                }
                onRemoveMedia={() => {
                  updateSlide({
                    ...slide,
                    media: {
                      type: "image",
                      path: "",
                    },
                  });
                }}
              />
            </div>
          </>
        );

      case "contentWithMediaLeft":
        return (
          <>
            <div className="col-span-2 mb-4">
              <Input
                value={slide.title}
                onChange={handleTitleChange}
                className="mb-2 w-full border-0 bg-transparent p-0 text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                style={{ color: slide.titleColor || theme.titleColor }}
                placeholder="Enter slide title..."
              />
              {"subtitle" in slide && (
                <Input
                  value={slide.subtitle}
                  onChange={handleSubtitleChange}
                  className="mb-4 w-full border-0 bg-transparent p-0 text-2xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Enter subtitle here..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
            <div className="flex flex-col items-center justify-center rounded-lg bg-gray-200 p-4">
              <FileUpload
                mediaType="image"
                onFileSelect={(mediaFile) => {
                  updateSlide({
                    ...slide,
                    media: {
                      type: mediaFile.type === "video" ? "video" : "image",
                      path: mediaFile.previewUrl,
                    },
                  });
                }}
                currentMedia={
                  slide.media &&
                  slide.media.path &&
                  slide.media.path.startsWith("blob:")
                    ? {
                        file: new File([], slide.media.type || "image"),
                        previewUrl: slide.media.path,
                        type: slide.media.type === "video" ? "video" : "image",
                      }
                    : undefined
                }
                onRemoveMedia={() => {
                  updateSlide({
                    ...slide,
                    media: {
                      type: "image",
                      path: "",
                    },
                  });
                }}
              />
            </div>
            <div>
              {"content" in slide && (
                <Textarea
                  value={slide.content}
                  onChange={handleContentChange}
                  className="h-full w-full resize-none border-0 bg-transparent p-0 text-xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Enter content here..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
          </>
        );

      case "imageWithCaption":
      case "videoWithCaption":
        return (
          <>
            <Input
              value={slide.title}
              onChange={handleTitleChange}
              className="mb-4 w-full border-0 bg-transparent p-0 text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
              style={{ color: slide.titleColor || theme.titleColor }}
              placeholder="Enter slide title..."
            />
            <div className="mb-4 flex h-40 w-full flex-col items-center justify-center rounded-lg bg-gray-200">
              {slide.layout === "imageWithCaption" ? (
                <FileUpload
                  mediaType="image"
                  onFileSelect={(mediaFile) => {
                    updateSlide({
                      ...slide,
                      image: {
                        type: "image",
                        path: mediaFile.previewUrl,
                      },
                    });
                  }}
                  currentMedia={
                    slide.image &&
                    slide.image.path &&
                    slide.image.path.startsWith("blob:")
                      ? {
                          file: new File([], "image"),
                          previewUrl: slide.image.path,
                          type: "image",
                        }
                      : undefined
                  }
                  onRemoveMedia={() => {
                    updateSlide({
                      ...slide,
                      image: {
                        type: "image",
                        path: "",
                      },
                    });
                  }}
                />
              ) : (
                <FileUpload
                  mediaType="video"
                  onFileSelect={(mediaFile) => {
                    updateSlide({
                      ...slide,
                      media: {
                        type: "video",
                        path: mediaFile.previewUrl,
                      },
                    });
                  }}
                  currentMedia={
                    slide.media &&
                    slide.media.path &&
                    slide.media.path.startsWith("blob:")
                      ? {
                          file: new File([], "video"),
                          previewUrl: slide.media.path,
                          type: "video",
                        }
                      : undefined
                  }
                  onRemoveMedia={() => {
                    updateSlide({
                      ...slide,
                      media: {
                        type: "video",
                        path: "",
                      },
                    });
                  }}
                />
              )}
            </div>
            {"caption" in slide && (
              <Textarea
                value={slide.caption}
                onChange={(e) =>
                  handleTextFieldChange("caption", e.target.value)
                }
                className="w-full resize-none border-0 bg-transparent p-0 text-xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                placeholder="Enter caption here..."
                style={{ color: slide.textColor || theme.textColor }}
              />
            )}
          </>
        );

      case "imageGrid":
        return (
          <>
            <Input
              value={slide.title}
              onChange={handleTitleChange}
              className="col-span-3 mb-6 w-full border-0 bg-transparent p-0 text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
              style={{ color: slide.titleColor || theme.titleColor }}
              placeholder="Enter slide title..."
            />
            {(
              [
                "cell_1_1",
                "cell_1_2",
                "cell_1_3",
                "cell_2_1",
                "cell_2_2",
                "cell_2_3",
              ] as Array<
                | "cell_1_1"
                | "cell_1_2"
                | "cell_1_3"
                | "cell_2_1"
                | "cell_2_2"
                | "cell_2_3"
              >
            ).map((cell) => (
              <div
                key={cell}
                className="flex flex-col items-center justify-center rounded-lg bg-gray-200 p-2"
              >
                {cell in slide && (
                  <div className="h-full w-full">
                    {slide[cell]?.path &&
                    slide[cell]?.path.startsWith("blob:") ? (
                      <div className="relative h-full w-full">
                        <img
                          src={slide[cell]?.path}
                          alt={`Image ${cell}`}
                          className="h-full w-full rounded-md object-cover"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="absolute right-2 top-2 h-8 w-8 rounded-full bg-white/70 p-0 hover:bg-white"
                          onClick={() => {
                            const updatedSlide = { ...slide };
                            updatedSlide[cell] = {
                              type: "image",
                              path: "",
                            };
                            updateSlide(updatedSlide);
                          }}
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    ) : (
                      <FileUpload
                        mediaType="image"
                        onFileSelect={(mediaFile) => {
                          const updatedSlide = { ...slide };
                          updatedSlide[cell] = {
                            type: "image",
                            path: mediaFile.previewUrl,
                          };
                          updateSlide(updatedSlide);
                        }}
                        currentMedia={
                          slide[cell] &&
                          slide[cell]?.path &&
                          slide[cell]?.path.startsWith("blob:")
                            ? {
                                file: new File([], "image"),
                                previewUrl: slide[cell]?.path || "",
                                type: "image",
                              }
                            : null
                        }
                        onRemoveMedia={() => {
                          const updatedSlide = { ...slide };
                          updatedSlide[cell] = {
                            type: "image",
                            path: "",
                          };
                          updateSlide(updatedSlide);
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </>
        );

      case "table":
        return (
          <>
            <Input
              value={slide.title}
              onChange={handleTitleChange}
              className="mb-2 w-full border-0 bg-transparent p-0 text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
              style={{ color: slide.titleColor || theme.titleColor }}
              placeholder="Enter slide title..."
            />
            {"subtitle" in slide && (
              <Input
                value={slide.subtitle}
                onChange={handleSubtitleChange}
                className="mb-4 w-full border-0 bg-transparent p-0 text-2xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                placeholder="Enter table subtitle here..."
                style={{ color: slide.textColor || theme.textColor }}
              />
            )}
            {"table" in slide && slide.table && (
              <div className="w-full overflow-x-auto rounded-lg bg-white p-2 shadow-inner">
                <table className="w-full border-collapse">
                  <tbody>
                    {slide.table.rows.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className={
                          rowIndex === 0
                            ? "bg-gray-800 text-white"
                            : rowIndex % 2 === 0
                              ? "bg-gray-100"
                              : ""
                        }
                      >
                        {row.map((cell, cellIndex) => (
                          <td
                            key={cellIndex}
                            className="border border-gray-300 p-1"
                          >
                            <Input
                              value={cell}
                              onChange={(e) => {
                                if (!slide.table) return;
                                const newRows = slide.table.rows.map((r, i) =>
                                  i === rowIndex
                                    ? r.map((c, j) =>
                                        j === cellIndex ? e.target.value : c,
                                      )
                                    : r,
                                );
                                updateSlide({
                                  ...slide,
                                  table: {
                                    ...slide.table,
                                    rows: newRows,
                                  },
                                });
                              }}
                              className="w-full border-0 bg-transparent p-0 text-sm outline-0 focus-visible:ring-0"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        );

      case "chartSingle":
        return (
          <>
            <Input
              value={slide.title}
              onChange={handleTitleChange}
              className="bg-gray-transparent mb-2 w-full border-0 p-0 text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
              style={{ color: slide.titleColor || theme.titleColor }}
              placeholder="Enter slide title..."
            />
            {"subtitle" in slide && (
              <Input
                value={slide.subtitle}
                onChange={handleSubtitleChange}
                className="mb-4 w-full border-0 bg-transparent p-0 text-2xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                placeholder="Enter chart subtitle here..."
                style={{ color: slide.textColor || theme.textColor }}
              />
            )}
            {"chart" in slide && slide.chart && (
              <div className="mb-4 w-full">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <BarChart4 className="h-5 w-5" />
                    <Select
                      value={slide.chart.type}
                      onValueChange={(value) => {
                        updateSlide({
                          ...slide,
                          chart: {
                            ...slide.chart,
                            type: value as string,
                            data: slide.chart?.data || [],
                          },
                        });
                      }}
                    >
                      <SelectTrigger className="w-[180px] bg-gray-700 p-2 text-white">
                        <SelectValue placeholder="Chart type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bar">Bar Chart</SelectItem>
                        <SelectItem value="line">Line Chart</SelectItem>
                        <SelectItem value="pie">Pie Chart</SelectItem>
                        <SelectItem value="doughnut">Doughnut Chart</SelectItem>
                        <SelectItem value="area">Area Chart</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  <div className="h-40 w-full overflow-hidden rounded-lg bg-gray-200 p-2">
                    {renderChartPreview(slide.chart)}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label className="text-sm font-semibold">Chart Data</Label>
                    <div className="mt-2 rounded-lg bg-white p-2 shadow-inner">
                      <div className="flex flex-col gap-2">
                        <Label className="text-xs">Series Name</Label>
                        {slide.chart.data && slide.chart.data[0] && (
                          <Input
                            className="bg-gray-700 text-white"
                            value={slide.chart.data[0].name}
                            onChange={(e) => {
                              const updatedChart = { ...slide.chart };
                              if (updatedChart.data && updatedChart.data[0]) {
                                updatedChart.data[0].name = e.target.value;
                                updateSlide({
                                  ...slide,
                                  chart: {
                                    ...updatedChart,
                                    type: updatedChart.type || "line",
                                    data: updatedChart.data,
                                  },
                                });
                              }
                            }}
                            placeholder="Data series name..."
                          />
                        )}
                      </div>

                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Labels</Label>
                          {slide.chart.data && slide.chart.data[0] && (
                            <Textarea
                              value={slide.chart.data[0].labels.join(", ")}
                              onChange={(e) => {
                                const updatedChart = { ...slide.chart };
                                if (updatedChart.data && updatedChart.data[0]) {
                                  updatedChart.data[0].labels = e.target.value
                                    .split(",")
                                    .map((item) => item.trim());
                                  updateSlide({
                                    ...slide,
                                    chart: {
                                      ...updatedChart,
                                      type: updatedChart.type || "line",
                                      data: updatedChart.data,
                                    },
                                  });
                                }
                              }}
                              className="bg-gray-700 text-white"
                              placeholder="Label 1, Label 2, Label 3..."
                            />
                          )}
                        </div>
                        <div>
                          <Label className="text-xs">Values</Label>
                          {slide.chart.data && slide.chart.data[0] && (
                            <Textarea
                              value={slide.chart.data[0].values.join(", ")}
                              onChange={(e) => {
                                const updatedChart = { ...slide.chart };
                                if (updatedChart.data && updatedChart.data[0]) {
                                  updatedChart.data[0].values = e.target.value
                                    .split(",")
                                    .map((item) => Number(item.trim()) || 0);
                                  updateSlide({
                                    ...slide,
                                    chart: {
                                      ...updatedChart,
                                      type: updatedChart.type || "line",
                                      data: updatedChart.data,
                                    },
                                  });
                                }
                              }}
                              className="bg-gray-700 text-white"
                              placeholder="10, 20, 30..."
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {"explanationTitle" in slide && "explanationContent" in slide && (
              <div className="w-full">
                <Input
                  value={slide.explanationTitle}
                  onChange={(e) =>
                    handleTextFieldChange("explanationTitle", e.target.value)
                  }
                  className="mb-2 w-full border-0 bg-transparent p-0 text-xl font-semibold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Explanation title..."
                  style={{ color: slide.titleColor || theme.titleColor }}
                />
                <Textarea
                  value={slide.explanationContent}
                  onChange={(e) =>
                    handleTextFieldChange("explanationContent", e.target.value)
                  }
                  className="w-full resize-none border-0 bg-transparent p-0 text-base outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Explanation content..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              </div>
            )}
          </>
        );

      case "chartDual":
        return (
          <>
            <div className="col-span-2 mb-2">
              <Input
                value={slide.title}
                onChange={handleTitleChange}
                className="mb-2 w-full border-0 bg-transparent p-0 text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                style={{ color: slide.titleColor || theme.titleColor }}
                placeholder="Enter slide title..."
              />
            </div>

            <div>
              {"titleLeft" in slide && (
                <Input
                  value={slide.titleLeft}
                  onChange={(e) =>
                    handleTextFieldChange("titleLeft", e.target.value)
                  }
                  className="mb-2 w-full border-0 bg-transparent p-0 text-2xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  style={{ color: slide.titleColor || theme.titleColor }}
                  placeholder="Left chart title..."
                />
              )}

              {"chartLeft" in slide && slide.chartLeft && (
                <div className="mb-4 w-full">
                  <div className="mb-2 flex items-center gap-2">
                    <Select
                      value={slide.chartLeft.type}
                      onValueChange={(value) => {
                        const updatedSlide = { ...slide };
                        if (updatedSlide.chartLeft) {
                          updatedSlide.chartLeft = {
                            ...updatedSlide.chartLeft,
                            type: value as string,
                            data: updatedSlide.chartLeft.data || [],
                          };
                          updateSlide(updatedSlide as Slide);
                        }
                      }}
                    >
                      <SelectTrigger className="w-full bg-gray-700 text-white">
                        <SelectValue placeholder="Chart type" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-700 text-white">
                        <SelectItem value="pie">Pie Chart</SelectItem>
                        <SelectItem value="doughnut">Doughnut Chart</SelectItem>
                        <SelectItem value="bar">Bar Chart</SelectItem>
                        <SelectItem value="line">Line Chart</SelectItem>
                        <SelectItem value="area">Area Chart</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Live Chart Preview */}
                  <div className="mb-4 rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
                    <div className="h-32">
                      {renderChartPreview(slide.chartLeft)}
                    </div>
                  </div>

                  {slide.chartLeft.data &&
                    slide.chartLeft.data[0] &&
                    slide.chartLeft.data[0].labels && (
                      <div className="mt-2">
                        <div className="flex h-32 flex-col gap-1 overflow-y-auto">
                          {slide.chartLeft.data[0].labels.map((label, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <Input
                                value={label}
                                onChange={(e) => {
                                  const updatedChart = { ...slide.chartLeft };
                                  if (
                                    updatedChart &&
                                    updatedChart.data &&
                                    updatedChart.data[0]
                                  ) {
                                    updatedChart.data[0].labels[idx] =
                                      e.target.value;
                                    const updatedSlide = {
                                      ...slide,
                                      chartLeft: {
                                        ...updatedChart,
                                        type: updatedChart.type || "line",
                                        data: updatedChart.data,
                                      },
                                    };
                                    updateSlide(updatedSlide as Slide);
                                  }
                                }}
                                className="w-1/2 bg-gray-700 text-xs text-white"
                                placeholder={`Label ${idx + 1}`}
                              />
                              <Input
                                type="number"
                                value={
                                  slide.chartLeft &&
                                  slide.chartLeft.data &&
                                  slide.chartLeft.data[0]
                                    ? slide.chartLeft.data[0].values[idx]
                                    : 0
                                }
                                onChange={(e) => {
                                  const updatedChart = { ...slide.chartLeft };
                                  if (
                                    updatedChart &&
                                    updatedChart.data &&
                                    updatedChart.data[0]
                                  ) {
                                    updatedChart.data[0].values[idx] =
                                      Number(e.target.value) || 0;
                                    const updatedSlide = {
                                      ...slide,
                                      chartLeft: {
                                        ...updatedChart,
                                        type: updatedChart.type || "line",
                                        data: updatedChart.data,
                                      },
                                    };
                                    updateSlide(updatedSlide as Slide);
                                  }
                                }}
                                className="w-1/2 bg-gray-700 text-xs text-white"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              )}

              {"explanationLeft" in slide && (
                <Textarea
                  value={slide.explanationLeft}
                  onChange={(e) =>
                    handleTextFieldChange("explanationLeft", e.target.value)
                  }
                  className="w-full resize-none border-0 bg-transparent p-0 text-sm outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Left chart explanation..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>

            <div>
              {"titleRight" in slide && (
                <Input
                  value={slide.titleRight}
                  onChange={(e) =>
                    handleTextFieldChange("titleRight", e.target.value)
                  }
                  className="mb-2 w-full border-0 bg-transparent p-0 text-2xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  style={{ color: slide.titleColor || theme.titleColor }}
                  placeholder="Right chart title..."
                />
              )}

              {"chartRight" in slide && slide.chartRight && (
                <div className="mb-4 w-full">
                  <div className="mb-2 flex items-center gap-2">
                    <Select
                      value={slide.chartRight.type}
                      onValueChange={(value) => {
                        const updatedSlide = { ...slide };
                        if (updatedSlide.chartRight) {
                          updatedSlide.chartRight = {
                            ...updatedSlide.chartRight,
                            type: value as string,
                            data: updatedSlide.chartRight.data || [],
                          };
                          updateSlide(updatedSlide as Slide);
                        }
                      }}
                    >
                      <SelectTrigger className="w-full bg-gray-700 text-white">
                        <SelectValue placeholder="Chart type" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-700 text-white">
                        <SelectItem value="pie">Pie Chart</SelectItem>
                        <SelectItem value="doughnut">Doughnut Chart</SelectItem>
                        <SelectItem value="bar">Bar Chart</SelectItem>
                        <SelectItem value="line">Line Chart</SelectItem>
                        <SelectItem value="area">Area Chart</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Live Chart Preview */}
                  <div className="mb-4 rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
                    <div className="h-32">
                      {renderChartPreview(slide.chartRight)}
                    </div>
                  </div>

                  {slide.chartRight.data &&
                    slide.chartRight.data[0] &&
                    slide.chartRight.data[0].labels && (
                      <div className="mt-2">
                        <div className="flex h-32 flex-col gap-1 overflow-y-auto">
                          {slide.chartRight.data[0].labels.map((label, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <Input
                                value={label}
                                onChange={(e) => {
                                  const updatedChart = { ...slide.chartRight };
                                  if (
                                    updatedChart &&
                                    updatedChart.data &&
                                    updatedChart.data[0] &&
                                    updatedChart.data[0].labels
                                  ) {
                                    updatedChart.data[0].labels[idx] =
                                      e.target.value;
                                    const updatedSlide = {
                                      ...slide,
                                      chartRight: {
                                        ...updatedChart,
                                        type: updatedChart.type || "line",
                                        data: updatedChart.data,
                                      },
                                    };
                                    updateSlide(updatedSlide as Slide);
                                  }
                                }}
                                className="w-1/2 bg-gray-700 text-xs text-white"
                                placeholder={`Label ${idx + 1}`}
                              />
                              <Input
                                type="number"
                                value={
                                  slide.chartRight &&
                                  slide.chartRight.data &&
                                  slide.chartRight.data[0]
                                    ? slide.chartRight.data[0].values[idx]
                                    : 0
                                }
                                onChange={(e) => {
                                  const updatedChart = { ...slide.chartRight };
                                  if (
                                    updatedChart &&
                                    updatedChart.data &&
                                    updatedChart.data[0]
                                  ) {
                                    updatedChart.data[0].values[idx] =
                                      Number(e.target.value) || 0;
                                    const updatedSlide = {
                                      ...slide,
                                      chartRight: {
                                        ...updatedChart,
                                        type: updatedChart.type || "line",
                                        data: updatedChart.data,
                                      },
                                    };
                                    updateSlide(updatedSlide as Slide);
                                  }
                                }}
                                className="w-1/2 bg-gray-700 text-xs text-white"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              )}

              {"explanationRight" in slide && (
                <Textarea
                  value={slide.explanationRight}
                  onChange={(e) =>
                    handleTextFieldChange("explanationRight", e.target.value)
                  }
                  className="w-full resize-none border-0 bg-transparent p-0 text-sm outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                  placeholder="Right chart explanation..."
                  style={{ color: slide.textColor || theme.textColor }}
                />
              )}
            </div>
          </>
        );
      case "dashboard":
        return (
          <div className="h-128 items-center justify-start overflow-y-auto">
            <Input
              value={slide.title}
              onChange={handleTitleChange}
              className="mb-6 w-full border-0 bg-transparent p-0 text-center text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
              style={{ color: slide.titleColor || theme.titleColor }}
              placeholder="Enter dashboard title..."
            />

            <div className="grid w-full flex-1 grid-cols-2 gap-4 px-2">
              {[1, 2, 3, 4].map((chartNum) => {
                const chartKey = `chart${chartNum}` as keyof typeof slide;
                const chartData = slide[chartKey] as ChartData | undefined;

                return (
                  <div
                    key={chartNum}
                    className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="bg-primary/10 flex h-6 w-6 items-center justify-center rounded-full">
                          <BarChart4 className="h-4 w-4 text-primary" />
                        </div>
                        <Input
                          value={
                            chartData?.data?.[0]?.name || `Chart ${chartNum}`
                          }
                          onChange={(e) => {
                            const updatedSlide = { ...slide };
                            // Explicitly type chartKey to help TypeScript
                            const key = chartKey as
                              | "chart1"
                              | "chart2"
                              | "chart3"
                              | "chart4";
                            const currentChartData = updatedSlide[key];

                            if (!currentChartData) {
                              updatedSlide[key] = {
                                type: "line",
                                data: [
                                  {
                                    name: e.target.value,
                                    labels: ["Jan", "Feb", "Mar", "Apr"],
                                    values: [25, 40, 30, 50],
                                  },
                                ],
                              };
                            } else if (currentChartData.data?.[0]) {
                              // Modify existing data directly
                              currentChartData.data[0].name = e.target.value;
                            }
                            updateSlide(updatedSlide);
                          }}
                          className="h-8 border-0 bg-transparent p-0 text-sm font-medium focus-visible:ring-0"
                          placeholder={`Chart ${chartNum} title...`}
                        />
                      </div>
                      <Select
                        value={chartData?.type || "line"}
                        onValueChange={(value) => {
                          const updatedSlide = { ...slide };
                          // Explicitly type chartKey
                          const key = chartKey as
                            | "chart1"
                            | "chart2"
                            | "chart3"
                            | "chart4";
                          const existingData = chartData?.data || [
                            {
                              name: `Dataset ${chartNum}`,
                              labels: ["Jan", "Feb", "Mar", "Apr"],
                              values: [25, 40, 30, 50],
                            },
                          ];

                          updatedSlide[key] = {
                            type: value,
                            data: [...existingData],
                          };

                          updateSlide(updatedSlide);
                        }}
                      >
                        <SelectTrigger className="h-7 w-[90px] border-dashed border-gray-300 bg-transparent text-xs">
                          <SelectValue placeholder="Chart type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="line">Line Chart</SelectItem>
                          <SelectItem value="bar">Bar Chart</SelectItem>
                          <SelectItem value="pie">Pie Chart</SelectItem>
                          <SelectItem value="doughnut">
                            Doughnut Chart
                          </SelectItem>
                          <SelectItem value="area">Area Chart</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="relative h-28 w-full overflow-hidden rounded-md bg-gray-50">
                      {chartData && chartData.data?.[0] ? (
                        <div className="absolute inset-0">
                          {renderChartPreview(chartData)}
                        </div>
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center">
                          <BarChart4 className="h-8 w-8 text-gray-300" />
                          <span className="mt-1 text-xs text-gray-400">
                            No chart data
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="mt-1 grid grid-cols-3 gap-1 overflow-x-auto text-xs">
                      {chartData?.data?.[0]?.labels
                        ?.slice(0, 6)
                        .map((label, idx) => (
                          <div key={idx} className="flex flex-col gap-1">
                            <Input
                              value={label}
                              onChange={(e) => {
                                const updatedSlide = { ...slide };
                                const chartToUpdate = updatedSlide[
                                  chartKey
                                ] as ChartData;
                                if (chartToUpdate?.data?.[0]) {
                                  chartToUpdate.data[0].labels[idx] =
                                    e.target.value;
                                  updateSlide(updatedSlide as Slide);
                                }
                              }}
                              className="h-6 border-dashed bg-transparent p-1 text-center text-xs"
                              placeholder={`Label ${idx + 1}`}
                            />
                            <Input
                              type="number"
                              value={chartData?.data?.[0]?.values?.[idx] || 0}
                              onChange={(e) => {
                                const updatedSlide = { ...slide };
                                const chartToUpdate = updatedSlide[
                                  chartKey
                                ] as ChartData;
                                if (chartToUpdate?.data?.[0]) {
                                  chartToUpdate.data[0].values[idx] = Number(
                                    e.target.value,
                                  );
                                  updateSlide(updatedSlide as Slide);
                                }
                              }}
                              className="h-6 border-dashed bg-transparent p-1 text-center text-xs"
                              placeholder={`Value ${idx + 1}`}
                            />
                          </div>
                        ))}
                    </div>

                    <div className="mt-auto pt-2 text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 border-dashed bg-transparent text-xs text-muted-foreground"
                        onClick={() => {
                          const updatedSlide = { ...slide };
                          // Explicitly type chartKey
                          const key = chartKey as
                            | "chart1"
                            | "chart2"
                            | "chart3"
                            | "chart4";
                          const chartToUpdate = updatedSlide[key];

                          if (!chartToUpdate?.data?.[0]) {
                            updatedSlide[key] = {
                              type: "line",
                              data: [
                                {
                                  name: `Chart ${chartNum}`,
                                  labels: ["Jan", "Feb", "Mar", "Apr"],
                                  values: [25, 40, 30, 50],
                                },
                              ],
                            };
                          } else {
                            // Modify existing data directly
                            const newLabel = `Item ${chartToUpdate.data[0].labels.length + 1}`;
                            chartToUpdate.data[0].labels.push(newLabel);
                            chartToUpdate.data[0].values.push(
                              Math.floor(Math.random() * 100),
                            );
                          }
                          updateSlide(updatedSlide);
                        }}
                      >
                        Add Data Point
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "processHorizontal":
        return (
          <>
            <Input
              value={slide.title}
              onChange={handleTitleChange}
              className="mb-6 w-full border-0 bg-transparent p-0 text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
              style={{ color: slide.titleColor || theme.titleColor }}
              placeholder="Enter process title..."
            />

            <div className="flex w-full flex-col gap-6">
              <div className="flex w-full items-center justify-between gap-2">
                <div className="w-full flex-1">
                  <Label className="mb-1 block text-sm">Step 1</Label>
                  <Input
                    value={slide.step1Box || ""}
                    onChange={(e) =>
                      handleTextFieldChange("step1Box", e.target.value)
                    }
                    className="bg-primary/10 mb-2 font-medium"
                    placeholder="Step 1 title"
                  />
                  <Textarea
                    value={slide.step1Text || ""}
                    onChange={(e) =>
                      handleTextFieldChange("step1Text", e.target.value)
                    }
                    className="min-h-[80px] bg-transparent text-sm"
                    placeholder="Step 1 description"
                    style={{ color: slide.textColor || theme.textColor }}
                  />
                </div>

                <div className="flex items-center text-muted-foreground">
                  <ArrowRight className="h-6 w-6" />
                </div>

                <div className="w-full flex-1">
                  <Label className="mb-1 block text-sm">Step 2</Label>
                  <Input
                    value={slide.step2Box || ""}
                    onChange={(e) =>
                      handleTextFieldChange("step2Box", e.target.value)
                    }
                    className="bg-primary/10 mb-2 font-medium"
                    placeholder="Step 2 title"
                  />
                  <Textarea
                    value={slide.step2Text || ""}
                    onChange={(e) =>
                      handleTextFieldChange("step2Text", e.target.value)
                    }
                    className="min-h-[80px] bg-transparent text-sm"
                    placeholder="Step 2 description"
                    style={{ color: slide.textColor || theme.textColor }}
                  />
                </div>
              </div>

              <div className="flex w-full items-center justify-between gap-2">
                <div className="w-full flex-1">
                  <Label className="mb-1 block text-sm">Step 3</Label>
                  <Input
                    value={slide.step3Box || ""}
                    onChange={(e) =>
                      handleTextFieldChange("step3Box", e.target.value)
                    }
                    className="bg-primary/10 mb-2 font-medium"
                    placeholder="Step 3 title"
                  />
                  <Textarea
                    value={slide.step3Text || ""}
                    onChange={(e) =>
                      handleTextFieldChange("step3Text", e.target.value)
                    }
                    className="min-h-[80px] bg-transparent text-sm"
                    placeholder="Step 3 description"
                    style={{ color: slide.textColor || theme.textColor }}
                  />
                </div>

                <div className="flex items-center text-muted-foreground">
                  <ArrowRight className="h-6 w-6" />
                </div>

                <div className="w-full flex-1">
                  <Label className="mb-1 block text-sm">Step 4</Label>
                  <Input
                    value={slide.step4Box || ""}
                    onChange={(e) =>
                      handleTextFieldChange("step4Box", e.target.value)
                    }
                    className="bg-primary/10 mb-2 font-medium"
                    placeholder="Step 4 title"
                  />
                  <Textarea
                    value={slide.step4Text || ""}
                    onChange={(e) =>
                      handleTextFieldChange("step4Text", e.target.value)
                    }
                    className="min-h-[80px] bg-transparent text-sm"
                    placeholder="Step 4 description"
                    style={{ color: slide.textColor || theme.textColor }}
                  />
                </div>
              </div>
            </div>
          </>
        );

      case "processVertical":
        return (
          <>
            <Input
              value={slide.title}
              onChange={handleTitleChange}
              className="mb-4 w-full border-0 bg-transparent p-0 text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
              style={{ color: slide.titleColor || theme.titleColor }}
              placeholder="Enter process title..."
            />

            <div className="h-128 flex w-full flex-col gap-2 overflow-y-auto">
              <div className="w-full">
                <Label className="mb-1 block text-sm">Step 1</Label>
                <Input
                  value={slide.step1Box || ""}
                  onChange={(e) =>
                    handleTextFieldChange("step1Box", e.target.value)
                  }
                  className="bg-primary/10 mb-2 font-medium"
                  placeholder="Step 1 title"
                />
                <Textarea
                  value={slide.step1Text || ""}
                  onChange={(e) =>
                    handleTextFieldChange("step1Text", e.target.value)
                  }
                  className="mb-2 min-h-[60px] bg-transparent text-sm"
                  placeholder="Step 1 description"
                  style={{ color: slide.textColor || theme.textColor }}
                />
              </div>

              <div className="flex justify-center">
                <ArrowDown className="h-6 w-6 text-muted-foreground" />
              </div>

              <div className="w-full">
                <Label className="mb-1 block text-sm">Step 2</Label>
                <Input
                  value={slide.step2Box || ""}
                  onChange={(e) =>
                    handleTextFieldChange("step2Box", e.target.value)
                  }
                  className="bg-primary/10 mb-2 font-medium"
                  placeholder="Step 2 title"
                />
                <Textarea
                  value={slide.step2Text || ""}
                  onChange={(e) =>
                    handleTextFieldChange("step2Text", e.target.value)
                  }
                  className="mb-2 min-h-[60px] bg-transparent text-sm"
                  placeholder="Step 2 description"
                  style={{ color: slide.textColor || theme.textColor }}
                />
              </div>

              <div className="flex justify-center">
                <ArrowDown className="h-6 w-6 text-muted-foreground" />
              </div>

              <div className="w-full">
                <Label className="mb-1 block text-sm">Step 3</Label>
                <Input
                  value={slide.step3Box || ""}
                  onChange={(e) =>
                    handleTextFieldChange("step3Box", e.target.value)
                  }
                  className="bg-primary/10 mb-2 font-medium"
                  placeholder="Step 3 title"
                />
                <Textarea
                  value={slide.step3Text || ""}
                  onChange={(e) =>
                    handleTextFieldChange("step3Text", e.target.value)
                  }
                  className="min-h-[60px] bg-transparent text-sm"
                  placeholder="Step 3 description"
                  style={{ color: slide.textColor || theme.textColor }}
                />
              </div>
            </div>
          </>
        );

      case "timelineHorizontal":
        return (
          <div className="flex flex-col gap-4">
            <Input
              value={slide.title}
              onChange={handleTitleChange}
              className="mb-6 w-full border-0 bg-transparent p-0 text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
              style={{ color: slide.titleColor || theme.titleColor }}
              placeholder="Enter timeline title..."
            />

            <div className="mt-10 px-8">
              {" "}
              {/* Added horizontal padding */}
              <div className="relative w-full">
                <div className="absolute left-0 right-0 top-10 h-1 bg-gray-300"></div>

                <div className="grid w-full grid-cols-4 gap-x-16">
                  {" "}
                  {/* Spread milestones evenly with gap */}
                  {[1, 2, 3, 4].map((num) => {
                    const upperKey =
                      `milestone${num}Upper` as keyof typeof slide;
                    const lowerKey =
                      `milestone${num}Lower` as keyof typeof slide;
                    const colorKey = `milestone${num}` as keyof typeof slide;

                    return (
                      <div
                        key={num}
                        className="z-10 flex flex-col items-center"
                      >
                        <Input
                          value={slide[upperKey]?.toString() || ""}
                          onChange={(e) =>
                            handleTextFieldChange(
                              upperKey as string,
                              e.target.value,
                            )
                          }
                          className="mb-4 max-w-[140px] border-0 bg-transparent p-0 text-center text-sm outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                          placeholder={`Milestone ${num}`}
                        />

                        <div
                          className="z-10 h-6 w-6 rounded-full"
                          style={{
                            backgroundColor:
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              (slide[colorKey] as any)?.color ||
                              theme.accentColor,
                          }}
                        ></div>

                        <Textarea
                          value={slide[lowerKey]?.toString() || ""}
                          onChange={(e) =>
                            handleTextFieldChange(
                              lowerKey as string,
                              e.target.value,
                            )
                          }
                          className="text-md mt-4 h-48 max-w-[140px] resize-none border-0 bg-transparent p-0 text-center outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                          placeholder={`Description ${num}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );

      case "timelineVertical":
        return (
          <div className="h-128 flex w-full flex-col gap-2 overflow-y-auto">
            <Input
              value={slide.title}
              onChange={handleTitleChange}
              className="mb-6 w-full border-0 bg-transparent p-0 text-4xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
              style={{ color: slide.titleColor || theme.titleColor }}
              placeholder="Enter timeline title..."
            />

            <div className="ml-6 mt-4">
              <div className="relative">
                <div className="absolute bottom-0 left-4 top-0 w-1 bg-gray-300"></div>

                <div className="flex flex-col gap-12">
                  {[1, 2, 3, 4].map((num) => {
                    const dateKey = `milestone${num}Date` as keyof typeof slide;
                    const textKey = `milestone${num}Text` as keyof typeof slide;
                    const colorKey = `milestone${num}` as keyof typeof slide;

                    return (
                      <div key={num} className="z-10 flex items-start">
                        <div
                          className="relative left-4 mr-6 h-6 w-6 -translate-x-1/2 rounded-full"
                          style={{
                            backgroundColor:
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              (slide[colorKey] as any)?.color ||
                              theme.accentColor,
                          }}
                        ></div>

                        <div className="flex-1">
                          <Input
                            value={slide[dateKey]?.toString() || ""}
                            onChange={(e) =>
                              handleTextFieldChange(
                                dateKey as string,
                                e.target.value,
                              )
                            }
                            className="mb-1 border-0 bg-transparent p-0 font-medium outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                            placeholder={`Date ${num}`}
                          />

                          <Textarea
                            value={slide[textKey]?.toString() || ""}
                            onChange={(e) =>
                              handleTextFieldChange(
                                textKey as string,
                                e.target.value,
                              )
                            }
                            className="resize-none border-0 bg-transparent p-0 text-sm outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                            placeholder={`Description ${num}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );

      case "contactInfo":
        return (
          <>
            {"thankYouText" in slide && (
              <Input
                value={slide.thankYouText}
                onChange={(e) =>
                  handleTextFieldChange("thankYouText", e.target.value)
                }
                className="mb-6 w-full border-0 bg-transparent p-0 text-5xl font-bold outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                placeholder="Thank you text..."
                style={{ color: slide.titleColor || theme.titleColor }}
              />
            )}
            {"contactInfo" in slide && (
              <Textarea
                value={slide.contactInfo}
                onChange={(e) =>
                  handleTextFieldChange("contactInfo", e.target.value)
                }
                className="w-full resize-none border-0 bg-transparent p-0 text-xl outline-1 outline-offset-2 focus-visible:outline-dashed focus-visible:ring-0"
                placeholder="Contact information..."
                style={{ color: slide.textColor || theme.textColor }}
              />
            )}
          </>
        );

      default:
        return (
          <div className="flex h-full w-full flex-col items-center justify-center">
            <div className="text-2xl font-semibold text-gray-500">
              Unsupported layout: {slide.layout}
            </div>
            <p className="mt-2 text-gray-400">
              Editor not implemented for this layout type.
            </p>
          </div>
        );
    }
  };

  return (
    <div className="w-full max-w-5xl pt-4">
      <div className="mb-6 flex items-center justify-between rounded-lg border border-secondary bg-sky-200 p-4 text-black shadow-md">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <Layout className="h-6 w-6" />
            <Label
              htmlFor="layout"
              className="text-lg font-semibold text-black"
            >
              Layout:
            </Label>
          </div>

          <Select value={slide.layout} onValueChange={handleLayoutChange}>
            <SelectTrigger className="w-[180px] border-secondary bg-background text-secondary-foreground">
              <SelectValue placeholder="Select layout" />
            </SelectTrigger>
            <SelectContent className="max-h-80 overflow-y-auto border-secondary bg-background text-secondary-foreground">
              <SelectItem value="standard">Standard</SelectItem>
              <SelectItem value="title-only">Title Only</SelectItem>
              <SelectItem value="title-content">Title & Content</SelectItem>
              <SelectItem value="split">Split</SelectItem>
              <SelectItem value="image-text">Image & Text</SelectItem>
              <Separator className="my-2" />
              <SelectItem value="titleSlide">Title Slide</SelectItem>
              <SelectItem value="contentFull">Content Full</SelectItem>
              <SelectItem value="contentTwoCol">Two Columns</SelectItem>
              <SelectItem value="contentThreeCol">Three Columns</SelectItem>
              <SelectItem value="contentFourCol">Four Columns</SelectItem>
              <SelectItem value="contentWithMediaRight">
                Content with Media Right
              </SelectItem>
              <SelectItem value="contentWithMediaLeft">
                Content with Media Left
              </SelectItem>
              <SelectItem value="imageWithCaption">
                Image with Caption
              </SelectItem>
              <SelectItem value="videoWithCaption">
                Video with Caption
              </SelectItem>
              <SelectItem value="imageGrid">Image Grid</SelectItem>
              <SelectItem value="table">Table</SelectItem>
              <SelectItem value="chartSingle">Single Chart</SelectItem>
              <SelectItem value="chartDual">Dual Charts</SelectItem>
              <SelectItem value="dashboard">Dashboard</SelectItem>
              <SelectItem value="processHorizontal">
                Horizontal Process
              </SelectItem>
              <SelectItem value="processVertical">Vertical Process</SelectItem>
              <SelectItem value="timelineHorizontal">
                Horizontal Timeline
              </SelectItem>
              <SelectItem value="timelineVertical">
                Vertical Timeline
              </SelectItem>
              <SelectItem value="contactInfo">Contact Info</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Badge
            variant="outline"
            className="border-secondary bg-secondary text-white"
          >
            <Type className="mr-1 h-3 w-3" />
            {theme.fontFamily.split(",")[0].replace(/['"]+/g, "")}
          </Badge>
          <Badge
            variant="outline"
            className="flex items-center border-secondary bg-secondary text-white"
          >
            <Palette className="mr-1 h-3 w-3" />
            <div
              className="mr-1 h-2 w-2 rounded-full"
              style={{ backgroundColor: theme.accentColor }}
            ></div>
            Accent
          </Badge>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full"
      >
        {/* 16:9 aspect ratio container for slide editing */}
        <div className="relative w-full pb-[56.25%]">
          <Card className="absolute inset-0 overflow-hidden rounded-xl border-0 shadow-lg">
            <CardContent
              className={`absolute inset-0 h-full p-8 ${getLayoutClass()}`}
              style={getSlideStyle()}
            >
              {renderSlideEditor()}
            </CardContent>
          </Card>
        </div>
      </motion.div>
    </div>
  );
}
